import {
  getMomentTz,
  MESApiService,
  type spParameter,
} from "@dexteel/mesf-core";
import type {
  StrandLossApiRow,
  StrandLossByShift,
} from "../models/StrandLossByShift";

const moment = getMomentTz();

interface ApiAssetRow {
  AssetId: number;
  ParentAssetId: number | null;
  AssetName: string;
}

interface ApiStrandLossCategoryRow {
  DelayCategoryId: number;
  DelayCategoryName: string;
  DelayCategoryColor: string;
}

interface ApiStrandLossCodeRow {
  DelayCodeId: number;
  ParentDelayCodeId: number | null;
  DelayCodeName: string;
  DelayCategoryId: number | null;
  IsBranch: boolean;
  IsSystemDelayCode: boolean;
}

const mapStrandLoss = (row: StrandLossApiRow): StrandLossByShift => ({
  StrandLossId: row.DelayId,
  AssetId: row.AssetId,
  AssetName: row.AssetName ?? null,
  StrandLossCodeId: row.DelayCodeId,
  StrandLossCodeName: row.DelayCodeName,
  Start: row.Start ? moment.utc(row.Start).toDate() : null,
  End: row.End ? moment.utc(row.End).toDate() : null,
  Duration: row.Duration ?? 0,
  ShiftId: row.ShiftId ?? 0,
  Comments: row.Comments ?? "",
  JustifiedByUser: row.JustifiedByUser ?? "",
  JustifiedDateTime: row.JustifiedDatetime
    ? moment.utc(row.JustifiedDatetime).toDate()
    : row.JustifiedDateTime
      ? moment.utc(row.JustifiedDateTime).toDate()
      : null,
  StrandLossCategoryId: row.DelayCategoryId,
  IsManual: row.IsManual ?? row.IsDelayManual ?? false,
  StrandLossCategoryName: row.DelayCategoryName ?? "",
  StrandLossCategoryColor: row.DelayCategoryColor ?? "",
  ProductionDate: row.ProductionDate
    ? moment.utc(row.ProductionDate).toDate()
    : null,
  ProductionShift: row.ProductionShift ?? 0,
  Shift: row.Shift ?? "",
  Crew: row.Crew ?? "",
  Heats:
    row.ProcessId != null && row.HeatName
      ? [{ ProcessId: row.ProcessId, HeatName: row.HeatName }]
      : [],
  Strand: row.Strand ?? null,
});

const mapStrandLosses = (rows: StrandLossApiRow[]): StrandLossByShift[] => {
  const strandLossesById = new Map<number | null, StrandLossByShift>();
  const processIdsByLossId = new Map<number | null, Set<number>>();

  rows.forEach((row) => {
    const strandLoss = mapStrandLoss(row);
    const existingStrandLoss = strandLossesById.get(strandLoss.StrandLossId);

    if (!existingStrandLoss) {
      strandLossesById.set(strandLoss.StrandLossId, strandLoss);
      processIdsByLossId.set(
        strandLoss.StrandLossId,
        new Set(strandLoss.Heats.map((heat) => heat.ProcessId)),
      );
      return;
    }

    const heat = strandLoss.Heats[0];
    const processIds = processIdsByLossId.get(strandLoss.StrandLossId);
    if (!heat || !processIds || processIds.has(heat.ProcessId)) return;

    processIds.add(heat.ProcessId);
    strandLossesById.set(strandLoss.StrandLossId, {
      ...existingStrandLoss,
      Heats: [...existingStrandLoss.Heats, heat],
    });
  });

  return [...strandLossesById.values()];
};

export const getStrandAssets = async (signal?: AbortSignal) => {
  const apiService = new MESApiService();
  const response = await apiService.callV2("[MES].[GetAssets]", [], signal);

  return {
    ...response,
    data: response.ok
      ? ((response.data?.tables?.[0]?.rows ?? []) as ApiAssetRow[])
      : [],
  };
};

export const getStrandLossesByShift = async (
  shiftId: number,
  includeMicroDelays: boolean,
  signal?: AbortSignal,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ShiftId", value: shiftId },
    { name: "IncludeMicroDelays", value: includeMicroDelays },
  ];
  const response = await apiService.callV2(
    "[RBD].[GetStrandLossesByShift]",
    parameters,
    signal,
  );

  return {
    ...response,
    data: response.ok
      ? mapStrandLosses(
          (response.data?.tables?.[0]?.rows ?? []) as StrandLossApiRow[],
        )
      : [],
  };
};

export const getStrandLossById = async (
  strandLossId: number,
  signal?: AbortSignal,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "DelayId", value: strandLossId }];
  const response = await apiService.callV2(
    "[DEL].[GetDelay]",
    parameters,
    signal,
  );
  if (!response.ok) return response;

  const row = (response.data.tables?.[0]?.rows?.[0] ??
    null) as StrandLossApiRow | null;

  return {
    ok: true as const,
    data: row ? mapStrandLoss(row) : null,
  };
};

export const getStrandLossCategories = async (
  parentAssetId: number,
  signal?: AbortSignal,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "DelayAreaAssetId", value: parentAssetId },
    { name: "IncludeAncestor", value: true },
  ];
  const response = await apiService.callV2(
    "[DEL].[GetDelayCategories]",
    parameters,
    signal,
  );
  if (!response.ok) return response;

  const rows = (response.data.tables?.[0]?.rows ??
    []) as ApiStrandLossCategoryRow[];

  return {
    ok: true as const,
    data: rows.map((row) => ({
      StrandLossCategoryId: row.DelayCategoryId,
      StrandLossCategoryName: row.DelayCategoryName,
      StrandLossCategoryColor: row.DelayCategoryColor,
    })),
  };
};

export const getStrandLossCodes = async (
  parentAssetId: number,
  isSystemDelayCode?: boolean,
  signal?: AbortSignal,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "DelayAreaAssetId", value: parentAssetId },
    { name: "IsSystemDelayCode", value: isSystemDelayCode ?? null },
  ];
  const response = await apiService.callV2(
    "[DEL].[GetDelayCodes]",
    parameters,
    signal,
  );
  if (!response.ok) return response;

  const rows = (response.data.tables?.[0]?.rows ??
    []) as ApiStrandLossCodeRow[];

  return {
    ok: true as const,
    data: rows.map((row) => ({
      StrandLossCodeId: row.DelayCodeId,
      ParentStrandLossCodeId: row.ParentDelayCodeId,
      StrandLossCodeName: row.DelayCodeName,
      StrandLossCategoryId: row.DelayCategoryId,
      IsLeaf: !row.IsBranch,
      IsSystemDelayCode: row.IsSystemDelayCode,
    })),
  };
};

export const upsertStrandLoss = async (
  strandLossId: number | null,
  assetId: number,
  start: Date | null,
  end: Date | null,
  strandLossCodeId: number | null,
  strandLossCategoryId: number | null,
  comments: string | null,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "DelayId", value: strandLossId },
    { name: "AssetId", value: assetId },
    { name: "Start", value: start ? start.toISOString() : null },
    { name: "End", value: end ? end.toISOString() : null },
    { name: "DelayCodeId", value: strandLossCodeId },
    { name: "DelayCategoryId", value: strandLossCategoryId },
    { name: "Comments", value: comments },
  ];

  return apiService.callV2("[DEL].[UpsertManualDelay]", parameters);
};

export const justifyStrandLoss = async (
  strandLossId: number,
  strandLossCodeId: number | null,
  strandLossCategoryId: number | null,
  comments: string | null,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "DelayId", value: strandLossId },
    { name: "DelayCodeId", value: strandLossCodeId },
    { name: "DelayCategoryId", value: strandLossCategoryId },
    { name: "Comments", value: comments },
  ];

  return apiService.callV2("[DEL].[JustifyDelay]", parameters);
};

export const deleteStrandLoss = async (strandLossId: number) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "DelayId", value: strandLossId }];

  return apiService.callV2("[DEL].[DeleteManualDelay]", parameters);
};
